import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  PriorAuthActionBody,
  PriorAuthCommandResponseT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import { submitPriorAuthAction } from '../api/priorAuthApi';
import { priorAuthKeys } from './keys';

export interface PriorAuthActionVariables {
  recordId: string;
  request: PriorAuthActionBody;
  /**
   * The patient's user id. When present the mutation also invalidates the
   * patient's prior-auth list query, so the tab picks up new rows (for
   * example, a freshly cancelled case moving to a terminal status).
   */
  patientUserId?: string;
}

export function usePriorAuthActionMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['priorAuth', 'action'],
    mutationFn: async ({
      recordId,
      request,
    }: PriorAuthActionVariables): Promise<PriorAuthCommandResponseT> => {
      return await submitPriorAuthAction(recordId, request);
    },
    onSuccess: async (_response, variables) => {
      const invalidations: Promise<unknown>[] = [
        queryClient.invalidateQueries({
          queryKey: priorAuthKeys.details(variables.recordId),
        }),
      ];
      if (variables.patientUserId) {
        invalidations.push(
          queryClient.invalidateQueries({
            queryKey: priorAuthKeys.patientList(variables.patientUserId),
          })
        );
      }
      await Promise.all(invalidations);
    },
  });
}
